//* Libraries imports
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

//* Components imports
import { ResourceQuestionList } from "./resource-question-list";
import { ErrorMessages } from "../../../../../../component/common/ErrorMessages";
import { toast } from "react-toastify";

//* Hooks imports
import { useGetPaginatedResourceQuestion } from "../../../../../../hooks/queries/resource-question/use-get-paginated-resource-question";
import { useDestroyResourceQuestion } from "../../../../../../hooks/mutations/resource-question/use-destroy-resource-question";

//* Types imports
import { type PaginatedResourceQuestion } from "../../../../../../schemas/common/resource/question/resource-question";


function transformResourceQuestionData(items: PaginatedResourceQuestion[]) {
  return items.map((item) => {
    let status: "active" | "pending" | "inactive" = "active";
    if (item.deadline) {
      const deadline = new Date(item.deadline.human.date);
      const now = new Date();
      if (deadline < now) {
        status = "inactive";
      } else {
        const timeDiff = deadline.getTime() - now.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        if (daysDiff <= 3) {
          status = "pending";
        }
      }
    }

    //! Convert numberOfQuestions to a number or string
    let numberOfQuestions: string | number = 0;
    if (Array.isArray(item.numberOfQuestions)) {
      numberOfQuestions = item.numberOfQuestions.length;
    } else {
      numberOfQuestions = item.numberOfQuestions || 0;
    }

    return {
      id: item.id,
      name: item.name || "Sem nome",
      description: item.description || "",
      deadline: item.deadline,
      numberOfQuestions,
      status,
      createdAt: item.createdAt,
    };
  });
}

export function ResourceQuestionTableNew() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const page = parseInt(searchParams.get("page") || "1", 10);
  const perPage = 10;
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const resourceQuestions = useGetPaginatedResourceQuestion(
    page,
    perPage,
    searchTerm
  );

  const destroyResourceQuestion = useDestroyResourceQuestion();

  useEffect(() => {
    if (resourceQuestions.isError && resourceQuestions.error) {
      const error = resourceQuestions.error as any;
      const message =
        typeof error === "string"
          ? error
          : error?.message ||
            "Ocorreu um erro ao buscar os recursos de questões.";
      setErrorMessages([message]);
    }
  }, [resourceQuestions.isError, resourceQuestions.error]);

  const clearErrorMessages = () => {
    setErrorMessages([]);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearch = () => {
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (searchTerm) {
        newSearchParams.set("search", searchTerm);
      } else {
        newSearchParams.delete("search");
      }
      newSearchParams.set("page", "1");
      setSearchParams(newSearchParams);
    }
  };

  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };

  const handleCreate = () => {
    navigate("question/create");
  };
    
  const handleEdit = (id: string) => {
    navigate(`question/edit/${id}`);
  };
    
  const handleDelete = (id: string) => {
    setIsDeleting(true);
    const deletePromise = new Promise((resolve, reject) => {
      destroyResourceQuestion.mutate(id, {
        onSuccess: () => {
          resolve("Recurso de questão excluído com sucesso!");
        },
        onError: (error) => {
          reject(error);
        },
        onSettled: async () => {
          await resourceQuestions.refetch();
          setIsDeleting(false);
        }
      });
    });

    toast.promise(deletePromise, {
      pending: "Excluindo recurso de questão...",
      success: "Recurso de questão excluído com sucesso!",
      error: "Ocorreu um erro ao excluir o recurso de questão."
    });
  };

  const handleView = (id: string) => {
    navigate(`questions/view/${id}`);
  };
  
  const transformedItems = resourceQuestions.data?.data 
    ? transformResourceQuestionData(resourceQuestions.data.data)
    : [];

  const totalItems = resourceQuestions.data?.meta?.totalItems || resourceQuestions.data?.data?.length || 0;
  const totalPages = Math.ceil(totalItems / perPage);
  const fields = [
    { name: 'name', header: 'Nome do Recurso', type: 'text' as const },
    // { name: 'numberOfQuestions', header: 'Nº Questões', type: 'text' as const },
    { name: 'deadline', header: 'Prazo Limite', type: 'date' as const },
    { name: 'status', header: 'Status', type: 'status' as const }
  ];

  return (
    <div className="w-100 p-5">
      <ErrorMessages 
        messages={errorMessages} 
        clearErrors={clearErrorMessages} 
      />
      
      <ResourceQuestionList
        title="Recursos de Questões"
        searchPlaceholder="Pesquisar recursos..."
        notFoundMessage="Nenhum recurso de questão encontrado"
        fields={fields}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onSearch={handleSearch}
        onCreate={handleCreate}
        onEditResourceQuestion={handleEdit}
        onDeleteResourceQuestion={handleDelete}
        onViewResourceQuestion={handleView}
        items={transformedItems}
        loading={resourceQuestions.isLoading || isDeleting}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={perPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
